import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Google Sign in with Google (GIS only — pops up the One Tap, and/or renders
 * the "Continue with Google" button). No `gapi`, no extra libraries.
 *
 * Pipeline (mirrors the codebase auth pattern — see `pages/Login.jsx`):
 *   script (loaded once) → `google.accounts.id.initialize` →
 *   `google.accounts.id.prompt()` (One Tap popup, when `autoPrompt`) +
 *   `renderButton` (when `renderButton`) → `onSuccess(gisResponse)` where
 *   `gisResponse.credential` is the Google ID token.
 *
 * The caller exchanges the credential at `POST /api/auth/google` and
 * establishes the session — use `userGoogleLogin(credential)` from
 * `store/user/userAuthStore.js`, which posts with `credentials: "include"`
 * and seeds `authSession` + the Zustand auth state.
 *
 * When `REACT_APP_GOOGLE_CLIENT_ID` is not set (or the GIS script fails to
 * load) and `renderButton` is on, a non-functional "Continue with Google"
 * placeholder is still rendered so the login/sign-up pages never lose the
 * button — clicking it surfaces the misconfiguration via `onError`.
 *
 * <GoogleOneTap
 *   onSuccess={handleSuccess}   // (gisResponse) => userGoogleLogin(gisResponse.credential)
 *   onError={handleError}
 *   enabled={!authenticated}
 *   autoPrompt            // popup on this page (public pages)
 *   renderButton          // button under the logo (login / register)
 *   width={320}
 * />
 */

const GSI_SCRIPT_URL = "https://accounts.google.com/gsi/client";

const BUTTON_OPTIONS = {
  type: "standard",
  theme: "outline",
  size: "large",
  text: "continue_with",
  shape: "rectangular",
  logo_alignment: "left",
};

// Module-scope promise → the script is injected exactly once, even under
// React StrictMode's double-mount (and several components using One Tap).
let gsiScriptPromise = null;

const GSI_TIMEOUT_MS = 15000;
const GSI_MAX_ATTEMPTS = 3;

/** Single injection attempt: resolves once `google.accounts.id` is usable. */
function injectGsiScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google);
      return;
    }

    const script = document.createElement("script");
    script.src = GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(value);
    };

    // Slow networks can hang the request for tens of seconds — bail out so a
    // retry can start instead of never firing the One Tap prompt at all.
    const timer = setTimeout(() => finish(reject, new Error("Google Identity Services timed out.")), GSI_TIMEOUT_MS);

    script.onload = () => {
      if (window.google?.accounts?.id) finish(resolve, window.google);
      else finish(reject, new Error("Google Identity Services loaded but not initialized."));
    };
    script.onerror = () => finish(reject, new Error("Failed to load Google Identity Services."));

    document.head.appendChild(script);
  });
}

// Retry a few times with backoff: accounts.google.com can be slow or briefly
// refuse the request depending on the network — one failed attempt must not
// permanently kill the popup for the whole session.
async function loadGsiScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("Window is not available."));
  if (window.google?.accounts?.id) return window.google;
  if (gsiScriptPromise) return gsiScriptPromise;

  gsiScriptPromise = (async () => {
    let lastError = null;
    for (let attempt = 1; attempt <= GSI_MAX_ATTEMPTS; attempt += 1) {
      try {
        return await injectGsiScript();
      } catch (err) {
        lastError = err;
        if (attempt < GSI_MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, attempt * 1000));
        }
      }
    }
    gsiScriptPromise = null; // allow the next mount to try again
    throw lastError || new Error("Failed to load Google Identity Services. Please try again.");
  })();

  return gsiScriptPromise;
}

/** Official multicolor "G" mark used by the native fallback button. */
function GoogleGLogo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function GoogleOneTap({
  onSuccess,
  onError,
  enabled = true,
  autoPrompt = true,
  renderButton = true,
  width = 320,
}) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  const buttonContainerRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const promptFiredRef = useRef(false);
  const [scriptFailed, setScriptFailed] = useState(false);

  // Keep fresh callbacks without putting them in the effect deps, so the One
  // Tap prompt / initialize never re-run because a new function identity
  // arrives on re-render.
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Hand the GIS response (with the JWT `credential`) to the caller, which
  // exchanges it at POST /api/auth/google and establishes the session.
  const handleCredential = useCallback((response) => {
    if (!response?.credential) {
      onErrorRef.current?.(new Error("Google sign-in was cancelled or returned no credential."));
      return;
    }
    onSuccessRef.current?.(response);
  }, []);

  // Initialize GIS once per mount; cancel on unmount (StrictMode-safe: cleanup
  // runs before the second effect, and `promptFiredRef` survives the
  // double-mount so the popup is never triggered twice).
  useEffect(() => {
    if (!enabled || !clientId) return undefined;

    let cancelled = false;
    let promptTimer = null;
    let buttonObserver = null;
    let responsiveButtonRender = null;

    // Fire One Tap only once the page has finished loading (and after a short
    // beat): prompting too early makes Chrome silently swallow the popup, so
    // the account card ("Continue as …@…") never appears.
    const promptWhenReady = () => {
      if (cancelled || !autoPrompt || promptFiredRef.current) return;
      promptFiredRef.current = true;
      window.google?.accounts?.id?.prompt();
    };
    const schedulePrompt = () => {
      if (cancelled || !autoPrompt || promptFiredRef.current) return;
      if (document.readyState === "complete") {
        promptTimer = setTimeout(promptWhenReady, 1200);
      } else {
        window.addEventListener("load", () => {
          promptTimer = setTimeout(promptWhenReady, 600);
        }, { once: true });
      }
    };

    loadGsiScript()
      .then((google) => {
        if (cancelled || !enabled) return;

        google.accounts.id.initialize({
          client_id: clientId,
          auto_select: false,
          context: "signin",
          ux_mode: "popup",
          callback: handleCredential,
        });

        if (renderButton && buttonContainerRef.current) {
          const container = buttonContainerRef.current;
          responsiveButtonRender = () => {
            if (cancelled || !container.isConnected) return;
            const availableWidth = container.clientWidth || width;
            container.innerHTML = "";
            google.accounts.id.renderButton(container, {
              ...BUTTON_OPTIONS,
              width: Math.max(200, Math.min(width, availableWidth)),
            });
          };

          responsiveButtonRender();
          if (typeof ResizeObserver !== "undefined") {
            buttonObserver = new ResizeObserver(responsiveButtonRender);
            buttonObserver.observe(container);
          } else {
            window.addEventListener("resize", responsiveButtonRender);
          }
        }

        setScriptFailed(false);
        schedulePrompt();
      })
      .catch((err) => {
        if (cancelled) return;
        setScriptFailed(true);
        onErrorRef.current?.(err);
      });

    return () => {
      cancelled = true;
      clearTimeout(promptTimer);
      buttonObserver?.disconnect();
      if (responsiveButtonRender) window.removeEventListener("resize", responsiveButtonRender);
      setScriptFailed(false);
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [enabled, clientId, autoPrompt, width, renderButton, handleCredential]);

  if (!enabled) return null;

  // Button requested, but no Web client ID configured (or GIS failed to load):
  // still show the button so it is always present — clicking it explains why.
  if (renderButton && (!clientId || scriptFailed)) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() =>
            onErrorRef.current?.(
              new Error(
                !clientId
                  ? "Google sign-in is not configured. Add REACT_APP_GOOGLE_CLIENT_ID to your .env."
                  : "Google is unavailable right now. Please try again."
              )
            )
          }
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          <GoogleGLogo />
          Continue with Google
        </button>
      </div>
    );
  }

  // Popup-only mode (One Tap): the popup floats top-right on its own — never
  // render an inline container, or an empty 44px box pushes the page header.
  if (!renderButton) return null;

  // Normal mode: GIS renders the official button into the container.
  return (
    <div
      ref={buttonContainerRef}
      className="flex w-full items-center justify-center overflow-hidden"
      style={{ minHeight: 44 }}
    />
  );
}
